/**
 * (owner, Sep 25: "easter egg: very rare place: area 69. heavily guarded big area surrounded by no roads. ufo props") The soldiers of
 * a place with `guards` (content/places/area_69.json): ordinary `Person`s with the role `soldier`, spawned with the place's chunk
 * like its parked vehicles (game.ts `spawnForChunk`) and gone with it, so a site nobody is near costs nothing.
 *
 * What they do, per site (the place's copy):
 *  - somebody within `warn` tiles of the fenced rect is told the sign (every WARN_EVERY seconds) and the nearest soldiers face them;
 *  - somebody inside the rect is a trespasser: the `enter` line once, `wanted` stars (and kept while inside), and every soldier with
 *    a clear line within ENGAGE tiles shoots — so does any of them at whoever hurt one of theirs, anywhere near;
 *  - otherwise a soldier walks their `route` round the site, or stands at their post facing out.
 * A trespasser stays hostile until they have been out of the fence and away for HOSTILE_FOR seconds. The soldiers marked `chase`
 * go after one who ran — out through the gate and up to CHASE_OUT tiles past the fence — and come back the same way. A break-in
 * makes the Sprawl FM news (content/radio/headline_area69.json). A place's vehicle `spawns` with a `route` drive it on their own
 * (`stepPatrol`: the jeep on its rounds inside the fence).
 *
 * (owner, Sep 25: "danger should be something that should be hard even with 4 online players co-oping") A place with a `garrison`
 * has an alarm: a trespasser or a shot soldier raises it, and it holds `alarm` seconds after the last raider was seen within `reach`.
 * While it is up every player in reach is a target and held at `wanted` stars, the crewed vehicles man their guns (`stepCrew`), and
 * waves come out of the `from` doors — `size` soldiers each, the `squad` loadouts in turn, every `every` seconds up to `max` alive.
 * The strength is fixed (owner: "the hardness should be static"): the same base for one raider or four, tuned so that four in
 * co-op find it hard (tools/area69-raid.ts). In a fight a soldier keeps a distance and works round to the
 * side it was given (a flank), and one who is hit or hurt runs for cover — a spot out of the raider's line — for a couple of seconds
 * before coming out again. A soldier's hit and rate of fire are content (`damage`, `rate`).
 */
import { smokeBetween } from './charges';
import type { Game } from './game';
import type { Place } from '../content/types';
import { makePerson, newId, type Person } from './entities';
import { updatePerson, updateVehicle } from './physics';
import type { VehicleEntity } from './entities';
import { report } from './newsfeed';
import type { Rng } from '../world/rng';

/** tiles a soldier opens fire from (a weapon with less range, less), and strays from the post (or the route) to close in */
const ENGAGE = 38, LEASH = 26;
/** (owner, Sep 25) the fight: the distance a soldier keeps, how long it stays in cover, how often it looks for a target again */
const KEEP_NEAR = 9, KEEP_FAR = 22, COVER_FOR = 2.2, THINK = 0.25;
/** the furthest a soldier with a long gun (a marksman's rifle, a launcher) opens fire from; an anti-air soldier's reach, height included */
const LONG = 90, AA_REACH = 220;
/** seconds between two warnings to the same player, and how long a trespasser stays a target once out and away */
const WARN_EVERY = 8, HOSTILE_FOR = 40;
/** an aircraft this high over the fence is out of reach (and not trespassing on the ground) */
const HIGH = 45;
/** a soldier's shot by default: this much damage and shots a second (content: a guard's `damage` and `rate`), and a wobble in radians */
const SHOT_DMG = 8, SHOT_RATE = 1.4, WOBBLE = 0.14;
/** (owner, Sep 25) a chaser follows a hostile player within this many tiles, no further than CHASE_OUT tiles past the fence */
const CHASE_REACH = 70, CHASE_OUT = 30;
/** seconds between two break-in headlines from one site */
const NEWS_EVERY = 600;

export interface SentryState {
  /** the place copy's key: the soldiers of one site share their memory */
  site: string;
  /** the fenced rect, world tiles */
  rect: [number, number, number, number];
  warn: number; wanted: number; sign: string; enter: string;
  post: [number, number];
  route?: [number, number][];
  leg: number;
  cool: number;
  /** goes out after a trespasser who ran */
  chase?: boolean;
  /** the gate: a point just inside it and one just outside (world) */
  gate?: [number, number, number, number];
  /** shots a second */
  rate: number;
  /** (the fight) the way it works round a raider (+1 / −1), where it is taking cover and until when, when it looks again, whom it fights */
  side: number; cover?: [number, number]; coverUntil: number; think: number; tgt?: number; lastHp: number;
  /** came out with a wave: it roams (chases) wherever the raiders go within the garrison's reach */
  wave?: boolean;
  /** the post it mans (the guard's relative spot): manned again when an alarm is over */
  postKey?: string;
}

interface SiteMemory { warned: Map<number, number>; hostile: Map<number, number>; inside: Set<number>; starsAt: Map<number, number>; newsAt?: number;
  /** (owner, Sep 25) the garrison: its rules, the place's corner, a chunk key for its soldiers, the alarm, the last wave, the next loadout */
  garrison?: NonNullable<Place['garrison']>; origin?: [number, number]; home?: string; alarmUntil: number; waveAt: number; squadI: number;
  /** the site's fence and rules (a soldier's, kept so the alarm runs on when every soldier is down), the posts to man again after an alarm, whether it was up */
  proto?: SentryState; posts: { pl: Place; g: NonNullable<Place['guards']>[number] }[]; alarmOn?: boolean }
const memory = new WeakMap<Game, Map<string, SiteMemory>>();
function memOf(game: Game, site: string): SiteMemory {
  let m = memory.get(game); if (!m) { m = new Map(); memory.set(game, m); }
  let s = m.get(site); if (!s) { s = { warned: new Map(), hostile: new Map(), inside: new Set(), starsAt: new Map(), alarmUntil: -1e9, waveAt: -1e9, squadI: 0, posts: [] }; m.set(site, s); }
  return s;
}

/** a place copy's key: the grid cell for a repeating place, its id for one that stands once */
const siteKey = (pl: Place): string => pl.cell ?? pl.id;

/** The soldiers and the loot of a place whose spot falls in this chunk (called once per chunk spawn). */
export function spawnSentries(game: Game, pl: Place, x0: number, y0: number, size: number, home: string, rng: Rng): void {
  const inChunk = (x: number, y: number) => x >= x0 && x < x0 + size && y >= y0 && y < y0 + size;
  if (pl.garrison) { const m = memOf(game, siteKey(pl)); if (!m.garrison) { m.garrison = pl.garrison; m.origin = [pl.x, pl.y]; m.home = home; } }
  for (const g of pl.guards ?? []) {
    const x = pl.x + g.x, y = pl.y + g.y;
    if (!inChunk(x, y)) continue;
    postSoldier(game, pl, g, home, rng.float());
    if (pl.garrison) { const m = memOf(game, siteKey(pl)); if (!m.posts.some((o) => o.g === g)) m.posts.push({ pl, g }); }
  }
  for (const l of pl.loot ?? []) {
    const x = pl.x + l.x, y = pl.y + l.y;
    if (inChunk(x, y)) game.pickups.push({ id: newId(), x, y, weaponId: 'items', ammo: 0, home, t: 0, items: { ...l.items } });
  }
}

/** a guard at its post (or on its route) */
function postSoldier(game: Game, pl: Place, g: NonNullable<Place['guards']>[number], home: string, cool: number): Person {
  const x = pl.x + g.x, y = pl.y + g.y;
  const q = makeSoldier(game, pl, x, y, g.weapon, g.hp ?? 150, g.damage ?? SHOT_DMG, g.rate ?? SHOT_RATE, home, cool, !!g.chase);
  const s = q.sentry!; s.postKey = `${g.x},${g.y}`;
  if (g.route) { s.route = g.route.map(([rx, ry]) => [pl.x + rx, pl.y + ry] as [number, number]); let best = 0; s.route.forEach(([rx, ry], i) => { if (Math.hypot(rx - x, ry - y) < Math.hypot(s.route![best][0] - x, s.route![best][1] - y)) best = i; }); s.leg = best; }
  return q;
}

/** (owner, Sep 25) a vehicle locked while its site's alarm is up (the saucer: no flying out of a raid in it) */
export function lockedDown(game: Game, v: VehicleEntity): boolean { return !!v.lockdown && game.time < memOf(game, v.lockdown).alarmUntil; }

/** a soldier of this place at (x, y): the weapon capped to `damage` a hit, `rate` shots a second */
function makeSoldier(game: Game, pl: Place, x: number, y: number, weapon: string, hp: number, damage: number, rate: number, home: string, cool: number, chase: boolean): Person {
  const r = pl.restricted;
  const q = makePerson(x, y, false);
  q.role = 'soldier'; q.home = home; q.hp = q.maxHp = hp;
  const w = game.reg.get('weapon', weapon);
  if (w) q.weapons = [{ def: { ...w, damage }, mag: w.magazine, reserve: -1 }];
  q.items.cash = 20 + (q.id * 37) % 70;
  const gate = r?.gate ? gateOf(pl.x + r.x, pl.y + r.y, r.w, r.h, pl.x + r.gate[0], pl.y + r.gate[1]) : undefined;
  q.sentry = {
    site: siteKey(pl), ...(chase ? { chase: true } : {}), ...(gate ? { gate } : {}),
    rect: r ? [pl.x + r.x, pl.y + r.y, pl.x + r.x + r.w, pl.y + r.y + r.h] : [pl.x, pl.y, pl.x + pl.w, pl.y + pl.h],
    warn: r?.warn ?? 0, wanted: r?.wanted ?? 0, sign: r?.sign ?? '', enter: r?.enter ?? '',
    post: [x, y], leg: 0, cool, rate, side: q.id % 2 ? 1 : -1, coverUntil: 0, think: 0, lastHp: hp,
  };
  q.angle = outward(q.sentry, x, y);
  game.people.push(q);
  const m = memOf(game, q.sentry.site); if (!m.proto) m.proto = { ...q.sentry };
  return q;
}

/** the gate's two waypoints: three tiles in from the fence line and four out, on the side of the rect it is on */
function gateOf(x: number, y: number, w: number, h: number, gx: number, gy: number): [number, number, number, number] {
  const d = [gy - y, x + w - gx, y + h - gy, gx - x], side = d.indexOf(Math.min(...d)); // (north, east, south, west)
  const [nx, ny] = [[0, -1], [1, 0], [0, 1], [-1, 0]][side];
  return [gx - nx * 3, gy - ny * 3, gx + nx * 4, gy + ny * 4];
}

/** walk towards a goal, through the gate when the fence is between (a soldier who chased out comes back the same way) */
function goVia(game: Game, q: Person, s: SentryState, gx: number, gy: number, dt: number, run = false): number {
  const qin = outside(s, q.x, q.y) === 0, gin = outside(s, gx, gy) === 0;
  if (s.gate && qin !== gin) {
    const [ix, iy, ox, oy] = s.gate, [ax, ay] = qin ? [ix, iy] : [ox, oy], [bx, by] = qin ? [ox, oy] : [ix, iy];
    if (Math.hypot(q.x - ax, q.y - ay) > 1.2 && !(Math.hypot(q.x - bx, q.y - by) < Math.hypot(ax - bx, ay - by))) walkTo(game, q, ax, ay, dt, run);
    else walkTo(game, q, bx, by, dt, run);
    return Math.hypot(gx - q.x, gy - q.y);
  }
  return walkTo(game, q, gx, gy, dt, run);
}

/** facing away from the middle of the site: a post at the fence looks out over the desert */
function outward(s: SentryState, x: number, y: number): number {
  const mx = (s.rect[0] + s.rect[2]) / 2, my = (s.rect[1] + s.rect[3]) / 2;
  return Math.atan2(x - mx, -(y - my));
}

/** how far a point is outside the rect (0 inside) */
function outside(s: SentryState, x: number, y: number): number {
  const dx = Math.max(s.rect[0] - x, 0, x - s.rect[2]), dy = Math.max(s.rect[1] - y, 0, y - s.rect[3]);
  return Math.hypot(dx, dy);
}

/** a straight line free of walls at head height (the fence is 2 m, so a soldier sees over it: 2.2) */
function sightClear(game: Game, x0: number, y0: number, x1: number, y1: number): boolean {
  if (smokeBetween(game, x0, y0, x1, y1)) return false; // (Sep 30) nobody sees through a smoke grenade's cloud
  const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0));
  for (let i = 1; i < n; i++) { const t = i / n; if (game.world.solidAt(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, 2.2)) return false; }
  return true;
}

function walkTo(game: Game, p: Person, gx: number, gy: number, dt: number, run = false): number {
  const dx = gx - p.x, dy = gy - p.y, d = Math.hypot(dx, dy);
  if (d < 0.15) return d;
  updatePerson(game.world, p, dx / d, dy / d, run, dt, false, game.vehicles);
  p.angle = Math.atan2(dx, -dy);
  return d;
}

/** A player hurt one of the soldiers: the whole site is after them (and a garrison's alarm goes). */
export function provokeSentries(game: Game, hurt: Person, by: Person): void {
  if (!hurt.sentry || !by.isPlayer) return;
  const m = memOf(game, hurt.sentry.site);
  if (!m.hostile.has(by.id)) game.say('The soldiers open fire!', by);
  m.hostile.set(by.id, game.time);
}

export function stepSentries(game: Game, dt: number): void {
  const sites = new Map<string, Person[]>();
  for (const q of game.people) { if (!q.sentry || q.dead) continue; let l = sites.get(q.sentry.site); if (!l) sites.set(q.sentry.site, (l = [])); l.push(q); }
  // (owner, Sep 25) a garrison's alarm runs on with every soldier down: the waves still come
  const all = memory.get(game);
  if (all) for (const [site, m] of all) if (m.garrison && m.proto && !sites.has(site)) { const r = m.proto.rect; if (game.nearestPlayerDist((r[0] + r[2]) / 2, (r[1] + r[3]) / 2) < m.garrison.reach) sites.set(site, []); }
  for (const [site, soldiers] of sites) {
    const m = memOf(game, site), s0 = soldiers[0]?.sentry ?? m.proto!, gar = m.garrison;
    const warned: Person[] = [];
    const cx = (s0.rect[0] + s0.rect[2]) / 2, cy = (s0.rect[1] + s0.rect[3]) / 2;
    const alarmWas = game.time < m.alarmUntil;
    const raiders: Person[] = [];
    for (const st of game.players.values()) {
      const p = st.person; if (p.dead) { m.hostile.delete(p.id); m.inside.delete(p.id); continue; }
      const alt = p.vehicle ? p.vehicle.alt : p.z;
      const out = alt > HIGH ? Infinity : outside(s0, p.x, p.y);
      const inReach = !!gar && alt <= HIGH && Math.hypot(p.x - cx, p.y - cy) < gar.reach;
      if (out === 0) {
        if (!m.inside.has(p.id)) {
          m.inside.add(p.id); game.say(s0.enter, p);
          if (game.time - (m.newsAt ?? -1e9) > NEWS_EVERY) { m.newsAt = game.time; report(game, 'area69', p.x, p.y, {}, st.name); } // (the radio's lines are fixed words: a rendered server can voice them)
        }
        m.hostile.set(p.id, game.time);
        // the stars, and kept while inside: a deed of `wanted` stars whatever the difficulty (police.ts `crime`'s floor)
        if (s0.wanted > 0 && game.time - (m.starsAt.get(p.id) ?? -1e9) > 1) { m.starsAt.set(p.id, game.time); game.crime(0, p, s0.wanted); }
      } else {
        m.inside.delete(p.id);
        const h = m.hostile.get(p.id);
        if (h !== undefined && out > s0.warn && game.time - h > HOSTILE_FOR && !(alarmWas && inReach)) m.hostile.delete(p.id);
        if (!m.hostile.has(p.id) && out <= s0.warn && !alarmWas) {
          warned.push(p);
          if (game.time - (m.warned.get(p.id) ?? -1e9) > WARN_EVERY) { m.warned.set(p.id, game.time); game.say(`⚠ ${s0.sign}`, p); }
        }
      }
      if (inReach && m.hostile.has(p.id)) raiders.push(p);
    }
    if (gar) stepAlarm(game, site, m, gar, soldiers, raiders, alarmWas, cx, cy);
    for (const q of soldiers) stepSoldier(game, q, m, warned, dt);
  }
  // the crewed vehicles of every site whose alarm is up
  for (const v of game.vehicles) if (v.crew) stepCrew(game, v, dt);
}

/**
 * (owner, Sep 25: "hard even with 4 online players co-oping") the garrison's alarm: raised by a raider (a trespasser, somebody who
 * shot a soldier) within reach, held `alarm` seconds after the last one was there; while it is up every player within reach is a
 * raider, held at `wanted` stars, and the waves come.
 */
function stepAlarm(game: Game, site: string, m: SiteMemory, gar: NonNullable<Place['garrison']>, soldiers: Person[], raiders: Person[], alarmWas: boolean, cx: number, cy: number): void {
  if (raiders.length) m.alarmUntil = game.time + gar.alarm;
  const on = game.time < m.alarmUntil;
  if (m.alarmOn && !on) { // (stood down: every post manned again — no wearing the garrison down a death at a time)
    for (const { pl, g } of m.posts) { const key = `${g.x},${g.y}`; if (!game.people.some((q) => !q.dead && q.sentry?.site === site && q.sentry.postKey === key)) postSoldier(game, pl, g, m.home ?? '', 1); }
    m.waveAt = -1e9;
  }
  m.alarmOn = on;
  if (!on) return;
  const near: Person[] = [];
  for (const st of game.players.values()) {
    const p = st.person; if (p.dead || (p.vehicle ? p.vehicle.alt : p.z) > HIGH || Math.hypot(p.x - cx, p.y - cy) >= gar.reach) continue;
    near.push(p); m.hostile.set(p.id, game.time);
    if (!alarmWas) game.say('🚨 ALARM — the base is on alert. Reinforcements are coming', p);
    if (gar.wanted > 0 && game.time - (m.starsAt.get(p.id) ?? -1e9) > 1) { m.starsAt.set(p.id, game.time); game.crime(0, p, gar.wanted); }
  }
  // a wave: `size` soldiers out of the doors in turn, while fewer than `max` of the waves' soldiers stand — the same for one raider or four
  if (!near.length || game.time - m.waveAt < gar.every || !m.origin) return;
  m.waveAt = game.time;
  const standing = soldiers.filter((q) => q.sentry?.wave).length;
  const n = Math.min(gar.size, gar.max - standing);
  const pl = game.world.gen.places.in(m.origin[0], m.origin[1], m.origin[0] + 1, m.origin[1] + 1, false).find((p) => (p.cell ?? p.id) === site);
  if (!pl || n <= 0) return;
  for (let i = 0; i < n; i++) {
    const [fx, fy] = gar.from[(m.squadI + i) % gar.from.length], kit = gar.squad[m.squadI % gar.squad.length];
    m.squadI++;
    const q = makeSoldier(game, pl, pl.x + fx + (game.npcRng.float() - 0.5) * 3, pl.y + fy + (game.npcRng.float() - 0.5) * 2, kit.weapon, kit.hp, kit.damage, kit.rate, m.home ?? '', game.npcRng.float(), true);
    q.sentry!.wave = true;
  }
  for (const p of near) game.say(`🚨 Reinforcements: ${n} more soldiers`, p);
}

/**
 * (owner, Sep 25) a crewed vehicle while its site's alarm is up: it turns on the nearest raider in reach with a clear line and fires
 * its weapon — the crew's `damage` a hit, `rate` shots a second, never while a soldier stands near the raider (a shell's splash).
 * A player who takes it throws the crew out: a soldier more on foot, beside it.
 */
function stepCrew(game: Game, v: VehicleEntity, dt: number): void {
  const c = v.crew!, m = memOf(game, c.site);
  if (v.wrecked) { v.crew = undefined; return; }
  if (v.driver) {
    if (v.driver.isPlayer && m.origin) { const pl = game.world.gen.places.in(m.origin[0], m.origin[1], m.origin[0] + 1, m.origin[1] + 1, false).find((p) => (p.cell ?? p.id) === c.site); if (pl) { const kit = m.garrison?.squad[0]; makeSoldier(game, pl, v.x + v.def.size[0] + 1, v.y, kit?.weapon ?? 'carbine', kit?.hp ?? 150, kit?.damage ?? SHOT_DMG, kit?.rate ?? SHOT_RATE, m.home ?? '', 1, true).sentry!.wave = true; } }
    v.crew = undefined; return;
  }
  c.cool = Math.max(0, c.cool - dt);
  if (game.time >= m.alarmUntil) return;
  const w = game.reg.get('weapon', c.weapon); if (!w) return;
  const range = Math.min(w.range, 90);
  let tgt: Person | undefined, bd = range;
  for (const st of game.players.values()) {
    const p = st.person; if (p.dead || !m.hostile.has(p.id) || (p.vehicle ? p.vehicle.alt : p.z) > HIGH) continue;
    const d = Math.hypot(p.x - v.x, p.y - v.y); if (d < bd && d > 4 && sightClear(game, v.x, v.y, p.x, p.y)) { bd = d; tgt = p; }
  }
  if (!tgt) return;
  let diff = Math.atan2(tgt.x - v.x, -(tgt.y - v.y)) - v.angle;
  while (diff > Math.PI) diff -= Math.PI * 2; while (diff < -Math.PI) diff += Math.PI * 2;
  v.angle += Math.sign(diff) * Math.min(Math.abs(diff), 1.2 * dt); // (the turret, as the whole hull: a slow traverse)
  const splash = w.splash ?? 0;
  if (c.cool > 0 || Math.abs(diff) > 0.12 || (splash > 0 && game.people.some((q) => q.role === 'soldier' && !q.dead && Math.hypot(q.x - tgt!.x, q.y - tgt!.y) < splash + 1))) return;
  const len = v.def.size[1] / 2 + 0.8;
  game.fireWeapon({ ...w, damage: c.damage }, v.x + Math.sin(v.angle) * len, v.y - Math.cos(v.angle) * len, v.angle + (game.npcRng.float() - 0.5) * WOBBLE, 1.8, -5);
  c.cool = 1 / c.rate * (0.8 + game.npcRng.float() * 0.4);
}

/** (owner, Sep 25: no landing on the crash site) an anti-air soldier: a raider in an aircraft within reach gets a snare pod */
function stepAntiAir(game: Game, q: Person, m: SiteMemory, dt: number): void {
  const s = q.sentry!, w = q.weapons[0];
  let tgt: VehicleEntity | undefined, bd = AA_REACH;
  for (const st of game.players.values()) {
    const p = st.person, v = p.vehicle; if (p.dead || !v || !v.def.flight || v.alt <= 2 || !m.hostile.has(p.id)) continue;
    const d = Math.hypot(v.x - q.x, v.y - q.y, v.alt); if (d < bd) { bd = d; tgt = v; }
  }
  if (!tgt) { if (Math.hypot(q.x - s.post[0], q.y - s.post[1]) > 0.6) goVia(game, q, s, s.post[0], s.post[1], dt); return; }
  q.angle = Math.atan2(tgt.x - q.x, -(tgt.y - q.y));
  if (w && s.cool <= 0) { game.fireWeapon(w.def, q.x + Math.sin(q.angle) * 0.6, q.y - Math.cos(q.angle) * 0.6, q.angle, game.personBase(q) + 1.5, q.id); s.cool = (1 / s.rate) * (0.8 + game.npcRng.float() * 0.4); }
}

/** a spot within a few tiles out of the raider's line (behind a building, a tower, a crate) — cover — or undefined */
function coverFrom(game: Game, q: Person, tx: number, ty: number): [number, number] | undefined {
  let best: [number, number] | undefined, bd = Infinity;
  for (const r of [2.5, 5, 8]) for (let k = 0; k < 10; k++) {
    const a = (k / 10) * Math.PI * 2 + r, x = q.x + Math.cos(a) * r, y = q.y + Math.sin(a) * r;
    if (game.world.solidAt(x, y, 1) || sightClear(game, x, y, tx, ty)) continue;
    const d = Math.hypot(x - q.x, y - q.y) + Math.hypot(x - tx, y - ty) * 0.05; // (the nearest, and not towards the raider)
    if (d < bd) { bd = d; best = [x, y]; }
  }
  return best;
}

function stepSoldier(game: Game, q: Person, m: SiteMemory, warned: Person[], dt: number): void {
  const s = q.sentry!;
  s.cool = Math.max(0, s.cool - dt);
  if (q.vehicle) return;
  const alarm = game.time < m.alarmUntil, gar = m.garrison;
  const hurt = q.hp < s.lastHp - 0.01; s.lastHp = q.hp;
  // the nearest hostile player in reach with a clear line — looked for again a few times a second, kept in between while it holds
  const w0 = q.weapons[0]?.def;
  if (w0?.homing === 'air') { stepAntiAir(game, q, m, dt); return; }
  let target: Person | undefined, bd = Math.min(LONG, Math.max(ENGAGE, (w0?.range ?? 60) * 0.6));
  const had = s.tgt !== undefined ? game.players.get(s.tgt)?.person : undefined;
  s.think -= dt;
  if (had && !had.dead && m.hostile.has(had.id) && s.think > 0) { const d = Math.hypot(had.x - q.x, had.y - q.y); if (d < bd + 6) { target = had; bd = d; } }
  if (!target || s.think <= 0) {
    s.think = THINK + game.npcRng.float() * 0.1; target = undefined;
    for (const st of game.players.values()) {
      const p = st.person; if (p.dead || !m.hostile.has(p.id)) continue;
      if ((p.vehicle ? p.vehicle.alt : p.z) > HIGH) continue;
      const d = Math.hypot(p.x - q.x, p.y - q.y); if (d >= bd || !sightClear(game, q.x, q.y, p.x, p.y)) continue;
      target = p; bd = d;
    }
    s.tgt = target?.id;
  }
  const home = s.route ? s.route[s.leg] : s.post;
  // a chaser's quarry: the nearest hostile player in reach who is not too far past the fence (in an alarm: anywhere in the garrison's reach)
  const out = alarm ? (gar?.reach ?? CHASE_OUT) : CHASE_OUT;
  let quarry: Person | undefined;
  if (s.chase) { let qd = alarm ? (gar?.reach ?? CHASE_REACH) : CHASE_REACH; for (const st of game.players.values()) { const p = st.person; if (p.dead || !m.hostile.has(p.id) || (p.vehicle ? p.vehicle.alt : p.z) > HIGH || outside(s, p.x, p.y) > out) continue; const d = Math.hypot(p.x - q.x, p.y - q.y); if (d < qd) { qd = d; quarry = p; } } }
  const roam = outside(s, q.x, q.y) < out; // (a chaser goes no further out than this)
  // (owner, Sep 25) hit, or badly hurt: to cover for a moment — out of the raider's line — then out again
  if (target && (hurt || q.hp < q.maxHp * 0.35) && game.time > s.coverUntil + 1.5) { const c = coverFrom(game, q, target.x, target.y); if (c) { s.cover = c; s.coverUntil = game.time + COVER_FOR + game.npcRng.float(); } }
  if (s.cover && game.time < s.coverUntil) { walkTo(game, q, s.cover[0], s.cover[1], dt, true); if (target) q.angle = Math.atan2(target.x - q.x, -(target.y - q.y)); return; }
  s.cover = undefined;
  if (target) {
    q.angle = Math.atan2(target.x - q.x, -(target.y - q.y));
    const w = q.weapons[0];
    const rocket = !!w && (w.def.splash ?? 0) > 0;
    if (w && s.cool <= 0 && !(rocket && bd < (w.def.splash ?? 0) + 3)) {
      game.fireWeapon(w.def, q.x + Math.sin(q.angle) * 0.6, q.y - Math.cos(q.angle) * 0.6, q.angle + (game.npcRng.float() - 0.5) * WOBBLE * 2, game.personBase(q) + 1.2, q.id);
      s.cool = (1 / s.rate) * (0.75 + game.npcRng.float() * 0.5);
    }
    // the fight: close in on somebody far off (a chaser through the gate and out, the rest never further than the leash from where they
    // belong), back off from somebody too close, and in between work round to its own side — a moving soldier, and a flank
    const free = (quarry === target && roam) || Math.hypot(q.x - home[0], q.y - home[1]) < LEASH || alarm;
    if (bd > KEEP_FAR) { if (quarry === target && roam) goVia(game, q, s, target.x, target.y, dt, true); else if (free) walkTo(game, q, target.x, target.y, dt, true); }
    else if (free) {
      const dx = (target.x - q.x) / bd, dy = (target.y - q.y) / bd, back = bd < KEEP_NEAR ? -1 : 0;
      const gx = q.x + (-dy * s.side + dx * back) * 3, gy = q.y + (dx * s.side + dy * back) * 3;
      if (walkTo(game, q, gx, gy, dt) < 0 || game.world.solidAt(gx, gy, 1)) s.side = -s.side;
      if (game.npcRng.float() < dt * 0.25) s.side = -s.side; // (now and then the other way)
    }
    return;
  }
  if (quarry && roam) { goVia(game, q, s, quarry.x, quarry.y, dt, true); return; }
  // a warned visitor near: the soldiers nearest them stop and watch
  const watch = warned.find((p) => Math.hypot(p.x - q.x, p.y - q.y) < 28);
  if (watch) { q.angle = Math.atan2(watch.x - q.x, -(watch.y - q.y)); return; }
  if (s.wave) return; // (a wave's soldier holds where the fight left it)
  if (s.route) {
    if (goVia(game, q, s, home[0], home[1], dt) < 0.8) s.leg = (s.leg + 1) % s.route.length;
    return;
  }
  if (Math.hypot(q.x - s.post[0], q.y - s.post[1]) > 0.6) goVia(game, q, s, s.post[0], s.post[1], dt, Math.hypot(q.x - s.post[0], q.y - s.post[1]) > 6);
  else q.angle = outward(s, s.post[0], s.post[1]);
}

/**
 * (owner, Sep 25: "a jeep that drives laps inside the fence") a vehicle on its rounds: it steers for the next point of its loop, slows
 * for the corners, stops for anybody standing in front of it, and backs off for a second when it is stuck. Nobody within 200 tiles:
 * it waits where it is. A player who gets in ends its rounds (game.ts drops the patrol once it has a driver).
 */
export function stepPatrol(game: Game, v: VehicleEntity, dt: number): void {
  const pt = v.patrol!;
  if (game.nearestPlayerDist(v.x, v.y) > 200) return;
  let [tx, ty] = pt.route[pt.leg];
  if (Math.hypot(tx - v.x, ty - v.y) < 3) { pt.leg = (pt.leg + 1) % pt.route.length; [tx, ty] = pt.route[pt.leg]; }
  let diff = Math.atan2(tx - v.x, -(ty - v.y)) - v.angle;
  while (diff > Math.PI) diff -= Math.PI * 2; while (diff < -Math.PI) diff += Math.PI * 2;
  const fx = Math.sin(v.angle), fy = -Math.cos(v.angle), reach = v.def.size[1] / 2 + 5;
  const ahead = (x: number, y: number, side: number) => { const dx = x - v.x, dy = y - v.y, along = dx * fx + dy * fy, across = Math.abs(dx * fy - dy * fx); return along > 0 && along < reach && across < side; };
  const blocked = game.people.some((q) => !q.dead && !q.vehicle && ahead(q.x, q.y, 1.8)) || game.vehicles.some((o) => o !== v && ahead(o.x, o.y, 2.6));
  if (pt.back > 0) { pt.back -= dt; updateVehicle(game.world, v, { throttle: -0.7, steer: -Math.sign(diff), up: false, down: false }, dt); return; }
  const want = blocked ? 0 : Math.abs(diff) > 0.5 || Math.hypot(tx - v.x, ty - v.y) < 10 ? 4 : 9; // (slow into a corner and round it: it keeps to its loop)
  const throttle = v.speed < want - 0.5 ? 1 : v.speed > want + 0.5 ? -1 : 0;
  updateVehicle(game.world, v, { throttle, steer: Math.max(-1, Math.min(1, diff * 2.5)), up: false, down: false }, dt);
  if (!blocked && Math.abs(v.speed) < 0.4) { pt.stuck += dt; if (pt.stuck > 2) { pt.stuck = 0; pt.back = 1.2; } } else pt.stuck = 0;
}
